import { useContext, useMemo } from 'react'
import moment from 'moment'

import { groupBy, sortBy } from 'lib/array/arrayUtils'
import useBedbankRates from 'hooks/useBedbankRates'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { isReservationAvailableForRoom } from 'lib/checkout/cartReservationUtils'
import { getPromotionMinLos, getBedbankCalendar, isFlashCalendarEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import getBedbankPromoExtraValue from 'lib/hotels/bedbankUtils'
import { useAppSelector } from './reduxHooks'
import { SALES_ENVIRONMENT_ENUM } from 'constants/bedbank'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from './Optimizely/useOptimizelyExperiment'

function useBedbankPackageViews(
  offer: App.BedbankOffer,
  region: string,
  currency: string,
  rooms: Array<App.Occupants>,
  duration: number,
  checkIn?: moment.Moment,
  checkOut?: moment.Moment,
): [Array<App.BedbankOfferPackageView>, boolean] {
  const [{ hotelOnlyRates, hotelBundleRates }, fetchingRates] = useBedbankRates(offer.id, rooms, checkIn, checkOut)
  const offerPageState = useContext(OfferPageStateContext)
  const promotionMinLos = useMemo(() => getPromotionMinLos(offer), [offer])
  const calendarData = useAppSelector((state) => getBedbankCalendar(offerPageState, state))
  const isCalendarEnabled = useAppSelector((state) => isFlashCalendarEnabled(offerPageState, state))

  const hasPromotions = offer.promotions.length > 0
  const promotions = useBedbankRoomRatePromotions(offer.promotions, checkIn, checkOut)
  const hasValidPromotions = Object.values(promotions).length
  const validCheckinCheckoutDates = !!(checkIn && checkOut)

  const optionSortExperiment = !!useOptimizelyExperiment(OptimizelyExperiments.croBedbankOptionSortOrder)

  const result = useMemo(() => {
    const allPackages = [...offer.packages, ...hotelOnlyRates.map(rate => rate.room)]
    const roomTypes = Array.from(new Map(allPackages.map(pkg => ([pkg.roomId, pkg]))).values())
    const ratesGroup = groupBy(hotelOnlyRates, (rate) => rate.roomId)
    const packageViews = roomTypes.map(pkg => {
      const roomRates = hotelOnlyRates.filter(rate => rate.roomId === pkg.roomId)
      let experiementOptionId: string | undefined
      if (optionSortExperiment) {
        // This experiment wants to place the cheapest refundable rate with breakfast at the to
        const refundableAndBreakfastRates = roomRates.filter(rate => {
          const hasBreakfastInclusion = rate.facilities.concat(rate.promotions).some(inclusion => inclusion.toLowerCase().includes('breakfast'))
          return rate.refundable && hasBreakfastInclusion
        })
        // so we find the cheapest rates' ID and then push that to the top in the actual sort by below
        experiementOptionId = sortBy(refundableAndBreakfastRates, rate => rate.totals.inclusive + (rate.totals.propertyFees ?? 0), 'asc')[0]?.id
      }
      let sortedRates = sortBy(roomRates, rate => {
        const price = rate.totals.inclusive + (rate.totals.propertyFees ?? 0)
        if (rate.id === experiementOptionId) {
          // If this is the experiement rate, then put it to the top (by making it a very small number)
          return price - 2000000
        }
        if (optionSortExperiment) {
          const hasBreakfastInclusion = rate.facilities.concat(rate.promotions).some(inclusion => inclusion.toLowerCase().includes('breakfast'))
          if (rate.refundable && hasBreakfastInclusion) {
            return
          }
        }

        return price
      }, 'asc')

      let bundleRates = hotelBundleRates
      const promotionsRates = sortedRates.filter(rate => promotions[rate.id]?.id === pkg.promotionId).reduce((acc, rate) => {
        acc[rate.id] = promotions[rate.id]
        return acc
      }, {})

      const overrideValueAtPrice = hasPromotions && validCheckinCheckoutDates

      if (overrideValueAtPrice) {
        sortedRates = sortedRates.map((rate) => {
          if (promotions[rate.id]) {
            const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
              price: pkg.sell.price,
              value: pkg.sell.value,
              minLos: pkg.sell.los,
              duration,
            }) : 0
            const displayedHotelValue = inclusionsAmount + rate.totals.inclusive

            return {
              ...rate,
              value: Math.round(displayedHotelValue),
              discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
            }
          } else return rate
        })

        bundleRates = hotelBundleRates.map((bundleRate) => {
          if (promotions[bundleRate.id]) {
            const inclusionsAmount = pkg.sell ? getBedbankPromoExtraValue({
              price: pkg.sell.price,
              value: pkg.sell.value,
              minLos: pkg.sell.los,
              duration,
            }) : 0
            const displayedHotelValue = inclusionsAmount + bundleRate.totals.inclusive

            return {
              ...bundleRate,
              value: Math.round(displayedHotelValue),
              discount: Number((inclusionsAmount / displayedHotelValue).toFixed(2)),
            }
          } else return bundleRate
        })
      }

      const showAllRates = promotionMinLos > duration && isCalendarEnabled

      const showOnlyPromo = hasPromotions && !validCheckinCheckoutDates

      let show = false

      if (showOnlyPromo && showAllRates) {
        show = true
      } else if (hasPromotions) {
        const promo = offer.promotions.find((p) => p.id === pkg.promotionId)
        if (!validCheckinCheckoutDates && promo) {
          if ((isCalendarEnabled && calendarData?.[SALES_ENVIRONMENT_ENUM.hotelOnly]?.rooms?.some(x => x.id === pkg.roomId)) || !isCalendarEnabled) {
            show = true
          }
        } else if (validCheckinCheckoutDates && promo && hasValidPromotions) {
          show = true
        } else if (validCheckinCheckoutDates && !hasValidPromotions) {
          show = true
        }
      } else {
        show = true
      }

      // if there is a valid and applicable promotion, no checkin/checkout dates is defined,
      // we should add a "ghost" package rate to display the promotion (or at least I think that's what this code is doing)
      if (showOnlyPromo && !showAllRates) {
        const promo = offer.promotions.find((p) => p.id === pkg.promotionId)
        if (promo && pkg.sell && promo.los <= duration) {
          const price = Math.round((pkg.sell.price / pkg.sell.los) * duration * rooms.length)
          const value = Math.round((pkg.sell.value / pkg.sell.los) * duration * rooms.length)
          const taxesAndFees = Math.round((pkg.sell.taxesAndFees / pkg.sell.los) * duration * rooms.length)
          const propertyFees = Math.round((pkg.sell.propertyFees / pkg.sell.los) * duration * rooms.length)
          const discount = Number(((pkg.sell.value - pkg.sell.price) / pkg.sell.value).toFixed(2))
          sortedRates.push({
            id: promo.id,
            bedGroups: [],
            facilities: [],
            regionCode: region,
            roomId: pkg.roomId,
            room: pkg,
            groupId: null,
            occupancyPricing: [],
            totals: {
              exclusive: price,
              inclusive: price,
              taxesAndFees,
              propertyFees,
            },
            value,
            nights: duration,
            discount: discount > 0.02 ? discount : 0,
            refundable: false,
            partiallyRefundable: false,
            isFlightBundle: false,
            corporate: false,
            promotions: [],
            cancellationPolicies: [],
            optionId: promo.id,
            currencyCode: currency,
            nonRefundableDateRanges: [],
            cancellationPolicy: {
              policies: [],
              description: ['If you cancel your booking, you are not eligible for a refund or credit for a future stay.'],
            },
          })
          promotionsRates[promo.id] = promo
        }
      }

      return {
        package: pkg,
        available: validCheckinCheckoutDates && ratesGroup.has(pkg.roomId) || !validCheckinCheckoutDates,
        capacityValid: rooms.every(room => isReservationAvailableForRoom(room, pkg.capacities.combinations, offer)),
        rates: sortedRates,
        bundleRates,
        cheapestRate: sortedRates[0],
        promotions: promotionsRates,
        show,
      }
    })
    return packageViews
  }, [
    hotelOnlyRates,
    hotelBundleRates,
    offer,
    promotionMinLos,
    duration,
    isCalendarEnabled,
    hasPromotions,
    validCheckinCheckoutDates,
    rooms,
    promotions,
    hasValidPromotions,
    calendarData,
    region,
    currency,
    optionSortExperiment,
  ])

  return [result, fetchingRates]
}

export default useBedbankPackageViews
